import type { Platform, PointerInput, Size } from '../core/types';

export function createH5Platform(canvas: HTMLCanvasElement): Platform {
  const resize = () => {
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
  };
  resize();
  window.addEventListener('resize', resize);

  let frameCb: ((t: number) => void) | null = null;
  let pointerCb: ((e: PointerInput) => void) | null = null;

  const loop = (t: number) => {
    if (frameCb) frameCb(t);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  const toLocal = (clientX: number, clientY: number) => {
    const rect = canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * canvas.width;
    const y = ((clientY - rect.top) / rect.height) * canvas.height;
    return { x, y };
  };

  const emit = (type: PointerInput['type'], clientX: number, clientY: number) => {
    if (!pointerCb) return;
    const { x, y } = toLocal(clientX, clientY);
    pointerCb({ type, x, y, t: performance.now() });
  };

  canvas.addEventListener(
    'pointerdown',
    (e) => {
      e.preventDefault();
      emit('down', e.clientX, e.clientY);
    },
    { passive: false },
  );
  canvas.addEventListener(
    'pointerup',
    (e) => {
      e.preventDefault();
      emit('up', e.clientX, e.clientY);
    },
    { passive: false },
  );
  canvas.addEventListener(
    'pointermove',
    (e) => {
      if (e.buttons) emit('move', e.clientX, e.clientY);
    },
    { passive: true },
  );

  return {
    kind: 'h5',
    canvas,
    now: () => performance.now(),
    onFrame(cb) {
      frameCb = cb;
    },
    onPointer(cb) {
      pointerCb = cb;
    },
    getSize(): Size {
      return { width: canvas.width, height: canvas.height };
    },
    storageGet(key) {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    storageSet(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch {
        /* ignore */
      }
    },
  };
}
